import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchWithAuth } from '@/data/api/client'
import type {
  TeamMembersListResponse,
  TeamMemberResponse,
  RoleUpdateRequest,
  InvitationsListResponse,
  InvitationResponse,
  InvitationCreateRequest,
  InvitationDetailsResponse,
  InvitationAcceptRequest,
  TransferOwnershipRequest,
  TransferOwnershipResponse,
} from '@/data/types/team.types'

async function listMembers(): Promise<TeamMembersListResponse> {
  return fetchWithAuth('/api/v1/team/members')
}

async function updateMemberRole(userId: string, data: RoleUpdateRequest): Promise<TeamMemberResponse> {
  return fetchWithAuth(`/api/v1/team/members/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

async function removeMember(userId: string): Promise<void> {
  return fetchWithAuth(`/api/v1/team/members/${userId}`, {
    method: 'DELETE',
  })
}

async function listInvitations(): Promise<InvitationsListResponse> {
  return fetchWithAuth('/api/v1/team/invitations')
}

async function createInvitation(data: InvitationCreateRequest): Promise<InvitationResponse> {
  return fetchWithAuth('/api/v1/team/invitations', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

async function cancelInvitation(invitationId: string): Promise<void> {
  return fetchWithAuth(`/api/v1/team/invitations/${invitationId}`, {
    method: 'DELETE',
  })
}

async function getInvitationDetails(token: string): Promise<InvitationDetailsResponse> {
  const params = new URLSearchParams({ token })
  return fetchWithAuth(`/api/v1/team/invitations/details?${params}`)
}

async function acceptInvitation(data: InvitationAcceptRequest): Promise<void> {
  return fetchWithAuth('/api/v1/team/invitations/accept', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

async function transferOwnership(data: TransferOwnershipRequest): Promise<TransferOwnershipResponse> {
  return fetchWithAuth('/api/v1/team/transfer-ownership', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function useTeamMembers() {
  return useQuery({
    queryKey: ['team', 'members'],
    queryFn: listMembers,
  })
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: RoleUpdateRequest }) =>
      updateMemberRole(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] })
    },
  })
}

export function useRemoveMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: removeMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] })
    },
  })
}

export function useInvitations() {
  return useQuery({
    queryKey: ['team', 'invitations'],
    queryFn: listInvitations,
  })
}

export function useCreateInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', 'invitations'] })
    },
  })
}

export function useCancelInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: cancelInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', 'invitations'] })
    },
  })
}

export function useInvitationDetails(token: string | null) {
  return useQuery({
    queryKey: ['team', 'invitations', 'details', token],
    queryFn: () => getInvitationDetails(token!),
    enabled: !!token,
  })
}

export function useAcceptInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: acceptInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', 'invitations'] })
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] })
    },
  })
}

export function useTransferOwnership() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: transferOwnership,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] })
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
    },
  })
}

